import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom'; 
import { useTranslation } from "react-i18next";
import '../styles/PatientAppointmentsView.css';
import FormatUrl from '../../../utils/UrlFormatter';
import { toast } from "react-toastify";
import { getClinicId } from '../../../utils/config';

const PatientAppointmentsView = ({ user }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [patientId, setPatientId] = useState(null);
    const [appointments, setAppointments] = useState([]);
    const [filteredAppointments, setFilteredAppointments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [appointmentsPerPage] = useState(6);
    const [statusFilter, setStatusFilter] = useState(""); 
    const [doctorSearch, setDoctorSearch] = useState(""); 

    const statusMap = {
        "CO": t("patient_appointments.completed"),
        "CA": t("patient_appointments.cancelled"),
        "AP": t("patient_appointments.approved"),
        "BO": t("patient_appointments.booked"),
        "PW": t("patient_appointments.completed"),
        "PC": t("patient_appointments.completed"),
    };

    useEffect(() => {
        if (!user || !user.patient_id) {
            setLoading(true);
            return;
        }
        setPatientId(user.patient_id);
    }, [user]);

    useEffect(() => {
        const fetchAppointments = async () => {
            if (!patientId) {
                setError("Patient ID is missing.");
                setLoading(false);
                return;
            }

            setLoading(true);
            try {
                const csrfUrl = FormatUrl(`/csrf-token/`);
                const csrfResponse = await fetch(csrfUrl, {
                    method: 'GET',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                });

                if (!csrfResponse.ok) throw new Error('Failed to fetch CSRF token');

                const csrfData = await csrfResponse.json();
                const csrfToken = csrfData.csrfToken;

                const apiUrl = FormatUrl(`/get-patient-appointments/?patient_id=${patientId}`);
                const response = await fetch(apiUrl, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRFToken': csrfToken,
                    },
                    credentials: 'include',
                });

                if (!response.ok) throw new Error('Failed to fetch appointments');

                const data = await response.json();
                setAppointments(data.appointments || []);
                setError(null);
            } catch (error) {
                setError(error.message);
            } finally {
                setLoading(false);
            }
        };

        if (patientId) {
            fetchAppointments(); 
        }
    }, [patientId]);

    useEffect(() => {
        let filtered = appointments.filter(appt =>
            ["BO", "CO", "CA", "PW", "PC"].includes(appt.status)
        );

        if (statusFilter) {
            filtered = filtered.filter(appointment => {
                if (statusFilter === "CO") {
                    return ["CO", "PW", "PC"].includes(appointment.status);
                }
                return appointment.status === statusFilter;
            });
        }        

        if (doctorSearch) {
            filtered = filtered.filter(appointment =>
                (`${appointment.doctor_first_name} ${appointment.doctor_last_name}`).toLowerCase().includes(doctorSearch.toLowerCase())
            );
        }

        filtered.sort((a, b) =>
            new Date(b.slot_start_time) - new Date(a.slot_start_time)
        );

        setFilteredAppointments(filtered);
        setCurrentPage(1);
    }, [statusFilter, doctorSearch, appointments]);

    const indexOfLastAppointment = currentPage * appointmentsPerPage;
    const indexOfFirstAppointment = indexOfLastAppointment - appointmentsPerPage;
    const currentAppointments = filteredAppointments.slice(indexOfFirstAppointment, indexOfLastAppointment);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);
    const nextPage = () => setCurrentPage(prev => (prev < Math.ceil(filteredAppointments.length / appointmentsPerPage) ? prev + 1 : prev));
    const prevPage = () => setCurrentPage(prev => (prev > 1 ? prev - 1 : prev));

    const handleCancelAppointment = async (appointmentId) => {
        try {
            const csrfApiUrl = FormatUrl('/csrf-token/');
            const csrfResponse = await fetch(csrfApiUrl, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
            });
    
            if (!csrfResponse.ok) throw new Error('Failed to fetch CSRF token');
    
            const csrfData = await csrfResponse.json();
            const csrfToken = csrfData.csrfToken;
            
            const clinicId = getClinicId(user);

            if (!clinicId) {
                console.error("Missing clinic ID");
                toast.error("Clinic information is missing.");
                return;
            }

            const cancelApiUrl = FormatUrl('/cancel-appointment/');
            const formData = new URLSearchParams();
            formData.append('appointment_id', appointmentId);
            formData.append('clinic_id', clinicId);
            formData.append('patient_id', user?.patient_id);
    
            const response = await fetch(cancelApiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                    'X-CSRFToken': csrfToken,
                },
                credentials: 'include',
                body: formData,
            });
    
            if (!response.ok) throw new Error('Cancel API failed');
    
            const result = await response.json();
    
            if (result.status === 'CA') {
                setAppointments((prev) =>
                    prev.map((app) =>
                        app.appointment_id === appointmentId ? { ...app, status: 'CA' } : app
                    )
                );
                setFilteredAppointments((prev) =>
                    prev.map((app) =>
                        app.appointment_id === appointmentId ? { ...app, status: 'CA' } : app
                    )
                );
                toast.success(t("patient_appointments.cancel_success"));
            } else {
                console.error("Failed to cancel:", result.error);
            }
        } catch (error) {
            console.error("Error cancelling appointment:", error.message);
            toast.error(t("patient_appointments.cancel_failure"));
        }
    };

    return (
        <div className="patient-apt-container">
            <h2 className="patient-apt-heading">{t("patient_appointments.your_appointments")}</h2>

            {loading && (
                <div className="patient-apt-loading-container">
                    <div className="patient-apt-spinner"></div>
                    <p className="patient-apt-loading-text">{t("patient_appointments.loading_appointments")}</p>
                </div>
            )}

            {error && <p className="error-message">{error}</p>}

            {!loading && !error && (
                appointments.length > 0 ? (
                    <>
                        <div className="patient-apt-filter-container">
                            <div className="patient-apt-filters">
                                <select 
                                    value={statusFilter} 
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    className="patient-apt-filter-select"
                                >
                                    <option value="">{t("patient_appointments.all_statuses")}</option>
                                    <option value="CO">{t("patient_appointments.completed")}</option>
                                    <option value="CA">{t("patient_appointments.cancelled")}</option>
                                    <option value="BO">{t("patient_appointments.booked")}</option>
                                </select>

                                <input
                                    type="text"
                                    placeholder={t("patient_appointments.search_practitioner")}
                                    value={doctorSearch}
                                    onChange={(e) => setDoctorSearch(e.target.value)}
                                    className="patient-apt-filter-input"
                                />
                            </div>
                            <button 
                                className="sched-back-button" 
                                onClick={() => navigate("/customer-scheduling")}
                            >
                                {t("patient_appointments.back_to_scheduling")}
                            </button>
                        </div>

                        <div className="patient-apt-grid">
                            {currentAppointments.length > 0 ? (
                                currentAppointments.map(appointment => (
                                    <div key={appointment.appointment_id} className="patient-apt-card">
                                        <h3>{appointment.doctor_first_name} {appointment.doctor_last_name}</h3>
                                        <p><strong>{t("patient_appointments.date")}:</strong> {new Date(appointment.slot_start_time).toLocaleDateString()}</p>
                                        <p><strong>{t("patient_appointments.time")}:</strong> {new Date(appointment.slot_start_time).toLocaleTimeString()} - {new Date(appointment.slot_end_time).toLocaleTimeString()}</p>
                                        <p className="patient-apt-status">
                                            <strong>{t("patient_appointments.status")}:</strong> {statusMap[appointment.status] || "Unknown"}
                                        </p>

                                        {!["CO", "CA", "PW"].includes(appointment.status) && (
                                            <button
                                                className="patient-apt-details-button"
                                                onClick={() => handleCancelAppointment(appointment.appointment_id)}
                                            >
                                                {t("patient_appointments.cancel")}
                                            </button>
                                        )}
                                    </div>
                                ))
                            ) : (
                                <p className="unlock-hint">
                                    {t("patient_appointments.no_previous_appointments_found")}
                                </p>
                            )}
                        </div>

                        <div className="patient-apt-pagination">
                            <button onClick={prevPage} disabled={currentPage === 1}>{t("patient_appointments.previous")}</button>
                            {Array.from({ length: Math.ceil(filteredAppointments.length / appointmentsPerPage) }, (_, i) => (
                                <button key={i + 1} onClick={() => paginate(i + 1)} className={currentPage === i + 1 ? "patient-apt-active" : ""}>
                                    {i + 1}
                                </button>
                            ))}
                            <button onClick={nextPage} disabled={currentPage === Math.ceil(filteredAppointments.length / appointmentsPerPage)}>{t("patient_appointments.next")}</button>
                        </div>
                    </>
                ) : (
                    <div className="patient-apt-empty-container">
                        <div className="patient-apt-empty-card">
                            <h3 className="patient-apt-empty-title">{t("patient_appointments.no_previous_appointments")}</h3>
                            <p className="patient-apt-empty-message">{t("patient_appointments.no_appointments_yet")}</p>
                            <p className="patient-apt-empty-suggestion">{t("patient_appointments.need_to_book")}</p>
                            <button 
                                className="patient-apt-book-button" 
                                onClick={() => navigate(user?.patient_id ? "/customer-scheduling" : "/dashboard")}
                            >
                                {t("patient_appointments.book_appointment")}
                            </button>
                        </div>
                    </div>
                )
            )}
        </div>
    );
};

export default PatientAppointmentsView;
